import { PatternPlayer } from "../clip/patternPlayer";
import type { ClipIR, PatternTrack } from "../clip/types";
import { clipOrderSteps, secondsPerStep } from "../clip/types";
import { instrumentsFromPresets } from "../kernel/drumKit";
import { createDefaultSlotMixes, normalizeSlotMixes } from "../kernel/slotMix";
import { normalizeSlotFxStacks } from "../kernel/slotFx";
import type { FxNode, Instrument, InstrumentSlot, SlotMix, SynthPreset } from "../types";
import { clampInstrumentSlotCount, MAX_INSTRUMENT_SLOT_COUNT } from "../types";
import { defaultSlotName } from "../kernel/defaultPresets";
import { createAudioEngine } from "./engine";
import { pushSf2BanksForInstruments } from "../sf2/pushBanks";
import { pushWavetablesForInstruments } from "../wavetable/pushTables";
import { pushSamplesForInstruments, type SampleResolver } from "../sampler/pushSamples";

export type BounceClipOptions = {
  clip: ClipIR;
  /** Preferred: full per-slot instruments (synth or drums). */
  instruments?: Instrument[];
  /** Legacy: synth presets only (wrapped as synth instruments). */
  presets?: SynthPreset[];
  /** Per-slot volume/pan (defaults to unity / center). */
  slotMix?: SlotMix[];
  /** Per-slot insert FX stacks. */
  slotFx?: FxNode[][];
  effects?: FxNode[];
  sampleRate?: number;
  /**
   * Extra seconds after the pattern for release / delay / reverb tails.
   * Default: enough for a max Cxx gate plus ~3s FX decay.
   */
  tailSeconds?: number;
  /** Offline render quantum (seconds). Smaller = smoother progress UI. */
  chunkSeconds?: number;
  onProgress?: (ratio: number) => void;
  /** Optional music mute/solo masks applied during the bounce. */
  muted?: boolean[];
  solo?: boolean[];
  /**
   * Resolve imported/user sample PCM for sampler instruments (OPFS/host).
   * Factory cycles load without this; omit and user samples stay silent.
   */
  resolveSample?: SampleResolver;
  /** Base URL for wavetable / SF2 asset fetches (defaults inside push helpers). */
  assetsBaseUrl?: string;
};

export type BounceClipResult = {
  /** Interleaved stereo samples (LRLR…). */
  samples: Float32Array;
  sampleRate: number;
  channels: 2;
  /** Pattern length only (excludes tail). */
  patternSec: number;
  /** Total rendered length including tail. */
  durationSec: number;
};

export type BounceStem = {
  slot: InstrumentSlot;
  /** Display / zip entry basename (without .wav). */
  name: string;
  /** Interleaved stereo samples (LRLR…). */
  samples: Float32Array;
  sampleRate: number;
  channels: 2;
  patternSec: number;
  durationSec: number;
};

export type BounceStemsResult = {
  stems: BounceStem[];
  sampleRate: number;
  patternSec: number;
  durationSec: number;
};

/**
 * Offline (non-realtime) bounce of one pattern cycle through the TS kernel.
 * Forces `loop: false`, schedules the full clip ahead of time, then renders
 * in chunks so the host can show progress.
 */
export async function bounceClipOffline(options: BounceClipOptions): Promise<BounceClipResult> {
  const sampleRate = options.sampleRate ?? 48000;
  const chunkSeconds = Math.max(0.05, options.chunkSeconds ?? 0.25);
  const clip: ClipIR = {
    ...structuredClone(options.clip),
    loop: false,
  };
  const stepSec = secondsPerStep(clip);
  const patternSec = Math.max(stepSec, clipOrderSteps(clip) * stepSec);
  // Max tracker cut is 16 steps past a hit; keep FX decay after that.
  const tailSeconds = options.tailSeconds ?? Math.max(3, 16 * stepSec + 2);
  const durationSec = patternSec + tailSeconds;
  const totalFrames = Math.max(1, Math.ceil(durationSec * sampleRate));
  const samples = new Float32Array(totalFrames * 2);

  const engine = await createAudioEngine({ mode: "direct", sampleRate });
  try {
    const instruments =
      options.instruments
      ?? instrumentsFromPresets(options.presets ?? []);
    await pushSf2BanksForInstruments(engine, instruments);
    await pushWavetablesForInstruments(engine, instruments, options.assetsBaseUrl);
    await pushSamplesForInstruments(engine, instruments, options.resolveSample);
    // Use full project width — default normalize truncates to 8 and drops
    // inserts / mix on tracks 9+.
    const slotCount = bounceSlotCount(options, clip, instruments.length);
    const mixes = normalizeSlotMixes(
      options.slotMix ?? createDefaultSlotMixes(slotCount),
      slotCount,
    );
    engine.transport.setSlotMixes(mixes);
    engine.transport.setSlotFxStacks(
      normalizeSlotFxStacks(options.slotFx, slotCount),
    );
    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      const instrument = instruments[slot];
      if (instrument) {
        engine.transport.setInstrument(slot as InstrumentSlot, instrument);
      }
    }
    if (options.effects && options.effects.length > 0) {
      engine.transport.setFxStack(options.effects);
    } else {
      engine.transport.clearFx();
    }

    const player = new PatternPlayer(engine, {
      // Schedule the entire song in the first pass.
      lookahead: patternSec + 1,
      intervalMs: 60_000,
      manageTransport: true,
      syncBpm: true,
      advanceClock: false,
    });
    player.setMuteSolo({ muted: options.muted, solo: options.solo });
    player.start(clip);

    let writeAt = 0;
    let renderedSec = 0;
    while (writeAt < samples.length) {
      const framesLeft = Math.floor((samples.length - writeAt) / 2);
      const chunkSec = Math.min(chunkSeconds, framesLeft / sampleRate);
      const chunk = engine.renderOffline(chunkSec);
      const copy = Math.min(chunk.length, samples.length - writeAt);
      samples.set(chunk.subarray(0, copy), writeAt);
      writeAt += copy;
      renderedSec = Math.min(durationSec, (writeAt / 2) / sampleRate);
      options.onProgress?.(Math.min(1, renderedSec / durationSec));
      // Yield so the lab can paint progress without freezing the tab.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }

    player.stop();
    options.onProgress?.(1);
    return { samples, sampleRate, channels: 2, patternSec, durationSec };
  } finally {
    engine.dispose();
  }
}

/** True when any cell on the slot has a note or note-off in the clip's patterns. */
export function clipSlotHasContent(clip: ClipIR, slot: InstrumentSlot): boolean {
  for (const pattern of clip.patterns) {
    const track = pattern.tracks.find(t => t.slot === slot);
    if (!track) {
      continue;
    }
    for (const cell of track.cells) {
      if (cell.note !== undefined || cell.noteOff) {
        return true;
      }
    }
  }
  return false;
}

/** Prefer an explicit track name, else the preset/slot factory label. */
export function clipSlotLabel(
  clip: ClipIR,
  slot: InstrumentSlot,
  presets?: readonly SynthPreset[],
): string {
  for (const pattern of clip.patterns) {
    const track = pattern.tracks.find((t: PatternTrack) => t.slot === slot);
    if (track?.name?.trim()) {
      return track.name.trim();
    }
  }
  const presetName = presets?.[slot]?.name?.trim();
  if (presetName) {
    return presetName;
  }
  return defaultSlotName(slot);
}

export function sanitizeStemFilename(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^\w\-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || "track";
}

/**
 * Bounce each occupied instrument slot soloed into its own buffer (same length /
 * FX / tail as a full mix bounce). Empty slots are skipped.
 */
export async function bounceStemsOffline(
  options: BounceClipOptions,
): Promise<BounceStemsResult> {
  const clip: ClipIR = {
    ...structuredClone(options.clip),
    loop: false,
  };
  const slots = allContentSlots(clip);
  if (slots.length === 0) {
    throw new Error("No tracks with notes to bounce");
  }

  const stems: BounceStem[] = [];
  let patternSec = 0;
  let durationSec = 0;
  let sampleRate = options.sampleRate ?? 48000;

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const solo = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, (_, s) => s === slot);
    const muted = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
    const result = await bounceClipOffline({
      ...options,
      clip,
      muted,
      solo,
      onProgress: ratio => {
        options.onProgress?.((i + Math.max(0, Math.min(1, ratio))) / slots.length);
      },
    });
    patternSec = result.patternSec;
    durationSec = result.durationSec;
    sampleRate = result.sampleRate;
    stems.push({
      slot,
      name: clipSlotLabel(clip, slot, options.presets),
      samples: result.samples,
      sampleRate: result.sampleRate,
      channels: 2,
      patternSec: result.patternSec,
      durationSec: result.durationSec,
    });
  }

  options.onProgress?.(1);
  return { stems, sampleRate, patternSec, durationSec };
}

function allContentSlots(clip: ClipIR): InstrumentSlot[] {
  const out: InstrumentSlot[] = [];
  for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
    const s = slot as InstrumentSlot;
    if (clipSlotHasContent(clip, s)) {
      out.push(s);
    }
  }
  return out;
}

/** Widen bounce mix/FX arrays to the project track count (not the default 8). */
function bounceSlotCount(
  options: BounceClipOptions,
  clip: ClipIR,
  instrumentCount: number,
): number {
  let max = Math.max(1, instrumentCount);
  if (Array.isArray(options.slotMix)) {
    max = Math.max(max, options.slotMix.length);
  }
  if (Array.isArray(options.slotFx)) {
    max = Math.max(max, options.slotFx.length);
  }
  if (Array.isArray(options.presets)) {
    max = Math.max(max, options.presets.length);
  }
  for (const pattern of clip.patterns) {
    max = Math.max(max, pattern.tracks.length);
  }
  return clampInstrumentSlotCount(max);
}
